import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { Panel, PanelLabel } from "@/components/PageHeader";
import { ProgressBar } from "@/components/budget/BudgetBits";
import { STATUS_LABELS } from "@/lib/budget";
import type { DashboardResponse } from "@/lib/dashboard";
import { accountKindLabel } from "@/lib/finance";
import { formatDate, money, moneyIn } from "@/lib/format";

function Card({ title, link, to, testId, children }: { title: string; link: string; to: string; testId: string; children: ReactNode }) {
  return (
    <Panel testId={testId}>
      <div className="flex items-center justify-between"><PanelLabel>{title}</PanelLabel><Link to={to} className="text-sm font-semibold text-[#1B2A4A] underline underline-offset-4 hover:text-amber-700" data-testid={`${testId}-link`}>{link}</Link></div>
      <div className="mt-3">{children}</div>
    </Panel>
  );
}

const Empty = ({ text }: { text: string }) => <p className="py-6 text-center text-sm text-slate-500">{text}</p>;
const ddmm = (iso: string) => formatDate(iso).slice(0, 5);

export function RecentCard({ d }: { d: DashboardResponse }) {
  return (
    <Card title="Últimos movimientos" link="Ver todos" to="/movimientos" testId="recent-card">
      {d.recent.length === 0 ? <Empty text="Aún no registras movimientos." /> : (
        <ul className="divide-y divide-[#DDE1E8]">
          {d.recent.map((t) => (
            <li key={t.id} className="grid grid-cols-[52px_1fr_auto] items-center gap-3 py-3 text-sm sm:grid-cols-[60px_1.5fr_1fr_auto]" data-testid={`recent-${t.id}`}>
              <span className="text-slate-500">{ddmm(t.date)}</span>
              <span className="truncate font-semibold text-[#1B2A4A]">{t.description}</span>
              <span className="hidden truncate text-slate-500 sm:block">{t.category_name ?? (t.type === "income" ? "Ingreso" : t.type === "transfer" ? "Transferencia" : "—")}</span>
              <span className={`tabular font-semibold ${t.flow === "in" ? "text-[#1B6E4F]" : t.flow === "out" ? "text-[#B23A1E]" : "text-slate-600"}`}>{t.flow === "in" ? "+" : t.flow === "out" ? "−" : ""}{money(t.flow ? Math.abs(t.amount) : t.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function BudgetCard({ d }: { d: DashboardResponse }) {
  return (
    <Card title="Presupuesto del mes" link="Ver presupuesto" to="/presupuesto" testId="budget-card">
      {d.budget.length === 0 ? <Empty text="Arma tu presupuesto para ver cómo vas." /> : (
        <ul className="grid gap-4 lg:grid-cols-2 lg:gap-x-10">
          {d.budget.map((c) => (
            <li key={c.id} data-testid={`budget-row-${c.id}`}>
              <div className="flex justify-between gap-2 text-sm"><span className="font-semibold text-[#1B2A4A]">{c.name}</span><span className="tabular text-slate-500">{money(c.actual_month_amount)} de {money(c.effective_limit)}</span></div>
              <div className="mt-1.5"><ProgressBar ratio={c.progress_ratio} marker={d.month_progress_ratio} status={c.status} /></div>
              <p className="mt-1 text-xs text-slate-500">{STATUS_LABELS[c.status]}</p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function UpcomingCard({ d }: { d: DashboardResponse }) {
  return (
    <Card title="Próximos" link="Ver calendario" to="/programados" testId="upcoming-card">
      {d.upcoming.length === 0 ? <Empty text="No tienes movimientos programados." /> : (
        <ul className="divide-y divide-[#DDE1E8]">
          {d.upcoming.map((o) => (
            <li key={`${o.schedule_id}-${o.date}`} className="grid grid-cols-[52px_1fr_auto] items-center gap-3 py-3 text-sm" data-testid={`upcoming-${o.schedule_id}`}>
              <span className="text-slate-500">{ddmm(o.date)}</span>
              <span className="truncate font-semibold text-[#1B2A4A]">{o.description} <span className="font-normal text-slate-400">· {o.label}</span></span>
              <span className={`tabular font-semibold ${o.type === "income" ? "text-[#1B6E4F]" : "text-[#B23A1E]"}`}>{o.type === "income" ? "+" : "−"}{money(o.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function AccountsCard({ d }: { d: DashboardResponse }) {
  return (
    <Card title="Cuentas" link="Ver patrimonio" to="/cuentas" testId="accounts-card">
      {d.accounts.length === 0 ? <Empty text="Agrega tus cuentas para ver tu patrimonio." /> : (
        <ul className="divide-y divide-[#DDE1E8]">
          {d.accounts.map((a) => (
            <li key={a.id} className="grid grid-cols-[1fr_auto] items-center gap-3 py-3 text-sm sm:grid-cols-[1.4fr_1fr_auto]" data-testid={`dash-account-${a.id}`}>
              <span className="truncate font-semibold text-[#1B2A4A]">{a.name}</span>
              <span className="hidden text-slate-500 sm:block">{accountKindLabel(a)}</span>
              <span className={`tabular font-semibold ${a.current_balance < 0 ? "text-[#B23A1E]" : "text-[#1B2A4A]"}`}>{moneyIn(a.current_balance, a.currency)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
